import React from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import DefaultButton from '../../../../../../global/default-button/default-button';
import { Popover } from '../../../../../../global/popover/popover';
import { HUB_MENU_ADD_ITEM_BUTTON_TEXT } from '../../../../../event-hubs.text';
import { useAnchoredCoords } from '../../_manage-page/_modules/module-type-picker/use-anchored-coords';
import { MenuItemType, MENU_ITEM_TYPE_OPTIONS, POPOVER_GAP, POPOVER_WIDTH } from './menu-item-type-picker.config';
import './menu-item-type-picker.scss';

type MenuItemTypePickerProps = {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
    onSelect: (type: MenuItemType) => void;
    disabled?: boolean;
    className?: string;
};

export { MenuItemType };

export const MenuItemTypePicker: React.FC<MenuItemTypePickerProps> = observer(props => {
    const { isOpen, onOpen, onClose, onSelect, disabled, className } = props;
    const { buttonRef, coords, isPopoverVisible } = useAnchoredCoords({
        isOpen: isOpen,
        onClose: onClose,
        popoverWidth: POPOVER_WIDTH,
        gap: POPOVER_GAP,
    });

    const handleButtonClick = isOpen ? onClose : onOpen;

    return (
        <div className={classnames('menu-item-type-picker', className, { 'menu-item-type-picker--open': isOpen })}>
            <DefaultButton
                ref={buttonRef}
                modifier='link'
                className='menu-item-type-picker__trigger'
                text={<FormattedMessage {...HUB_MENU_ADD_ITEM_BUTTON_TEXT} />}
                onClick={handleButtonClick}
                disabled={disabled}
                dataTestId='menu-item-add-button'
            />
            <Popover
                buttonRef={buttonRef}
                isOpen={isPopoverVisible}
                onClickOutside={onClose}
                customTop={coords?.top}
                customLeft={coords?.left}
                customWidth={POPOVER_WIDTH}
            >
                <ul className='menu-item-type-picker__options'>
                    {MENU_ITEM_TYPE_OPTIONS.map(option => (
                        <li
                            key={option.type}
                            className='menu-item-type-picker__option'
                            onClick={() => onSelect(option.type)}
                            data-test-id={`menu-item-type-${option.type}`}
                        >
                            <span className='menu-item-type-picker__option-name'>
                                <FormattedMessage {...option.nameText} />
                            </span>
                            <span className='menu-item-type-picker__option-description'>
                                <FormattedMessage {...option.descriptionText} />
                            </span>
                        </li>
                    ))}
                </ul>
            </Popover>
        </div>
    );
});
